import { useState, useEffect, useCallback } from 'react'
import { adminActivities } from '../api/client'
import { styles as as } from './adminStyles'

interface Props {
  onBack: () => void
}

interface WordLists {
  es: string[]
  en: string[]
}

function listToText(words: string[]): string {
  return words.join('\n')
}

function textToList(text: string): string[] {
  return text
    .split('\n')
    .map((w) => w.trim().toUpperCase())
    .filter((w) => w.length > 0)
}

function upperChange(
  e: React.ChangeEvent<HTMLTextAreaElement>,
  setter: (v: string) => void,
) {
  const el = e.target
  const start = el.selectionStart ?? el.value.length
  const end   = el.selectionEnd   ?? el.value.length
  setter(el.value.toUpperCase())
  requestAnimationFrame(() => {
    el.selectionStart = start
    el.selectionEnd   = end
  })
}

function upperInputChange(
  e: React.ChangeEvent<HTMLInputElement>,
  setter: (v: string) => void,
) {
  const el = e.target
  const start = el.selectionStart ?? el.value.length
  const end   = el.selectionEnd   ?? el.value.length
  setter(el.value.toUpperCase())
  requestAnimationFrame(() => {
    el.selectionStart = start
    el.selectionEnd   = end
  })
}

// Activities that share this word list — saved in parallel on every write
const WORD_ACTIVITIES = ['attention', 'crosswords'] as const

export default function AttentionWordEditor({ onBack }: Props) {
  const [lists,    setLists]    = useState<WordLists>({ es: [], en: [] })
  const [esText,   setEsText]   = useState('')
  const [enText,   setEnText]   = useState('')
  const [esSearch, setEsSearch] = useState('')
  const [enSearch, setEnSearch] = useState('')
  const [loading,  setLoading]  = useState(true)
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState<string | null>(null)
  const [savedOk,  setSavedOk]  = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    adminActivities.get('attention')
      .then((detail) => {
        const d = detail.data as { esWords?: string[]; enWords?: string[] } | null
        const es = d?.esWords ?? []
        const en = d?.enWords ?? []
        setLists({ es, en })
        setEsText(listToText(es))
        setEnText(listToText(en))
      })
      .catch(() => setError('Error al cargar las palabras'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const save = async () => {
    const esWords = textToList(esText)
    const enWords = textToList(enText)
    if (esWords.length < 50 || enWords.length < 50) {
      setError('Se necesitan al menos 50 palabras en cada idioma')
      return
    }
    setSaving(true)
    setError(null)
    setSavedOk(false)
    try {
      await Promise.all(
        WORD_ACTIVITIES.map(slug =>
          adminActivities.patch(slug, { data: { esWords, enWords } })
        )
      )
      setLists({ es: esWords, en: enWords })
      setSavedOk(true)
      setTimeout(() => setSavedOk(false), 3000)
    } catch {
      setError('Error al guardar las palabras')
    } finally {
      setSaving(false)
    }
  }

  const hasChanges =
    esText !== listToText(lists.es) ||
    enText !== listToText(lists.en)

  const esMatches = esSearch.length > 0
    ? lists.es.filter(w => w.includes(esSearch))
    : []
  const enMatches = enSearch.length > 0
    ? lists.en.filter(w => w.includes(enSearch))
    : []

  return (
    <div style={as.page}>
      <header style={as.header}>
        <button onClick={onBack} style={as.backBtn}>← Actividades</button>
        <h1 style={as.headerTitle}>Words</h1>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {savedOk && <span style={s.savedBadge}>✓ Guardado</span>}
          <button
            onClick={save}
            disabled={saving || loading || !hasChanges}
            style={{ ...as.submitBtn, opacity: (saving || loading || !hasChanges) ? 0.6 : 1, minHeight: 40 }}
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </header>

      <div style={{ ...as.content, maxWidth: 1100 }}>
        {error && <div style={s.errorBanner}>{error}</div>}

        {loading ? (
          <p style={as.emptyText}>Cargando…</p>
        ) : (
          <>
            <p style={s.hint}>
              Una palabra por línea. Las palabras se convierten a mayúsculas automáticamente.
              Se necesitan al menos 50 palabras por idioma (se recomiendan 150+).
            </p>

            <div style={s.grid}>
              {/* ES column */}
              <div style={s.col}>
                <div style={s.colHeader}>
                  🇪🇸 Español
                  <span style={s.count}>{textToList(esText).length} palabras</span>
                </div>
                <div style={s.searchRow}>
                  <span style={s.searchIcon}>🔍</span>
                  <input
                    type="text"
                    value={esSearch}
                    onChange={e => upperInputChange(e, setEsSearch)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && esSearch.length > 0 && esMatches.length === 0) {
                        setEsText(t => t ? t + '\n' + esSearch : esSearch)
                        setEsSearch('')
                      }
                    }}
                    placeholder="Buscar palabra existente…"
                    style={s.searchInput}
                    spellCheck={false}
                  />
                  {esSearch.length > 0 && (
                    <button onClick={() => setEsSearch('')} style={s.clearBtn}>✕</button>
                  )}
                </div>
                {esSearch.length > 0 && (
                  <div style={s.searchResults}>
                    {esMatches.length === 0
                      ? <span style={s.noMatch}>Sin coincidencias — pulsa Enter para añadir</span>
                      : esMatches.map(w => <span key={w} style={s.matchChip}>{w}</span>)
                    }
                  </div>
                )}
                <textarea
                  style={s.textarea}
                  value={esText}
                  onChange={(e) => upperChange(e, setEsText)}
                  placeholder={'SOL\nMAR\nCASA\n…'}
                  spellCheck={false}
                />
              </div>

              {/* EN column */}
              <div style={s.col}>
                <div style={s.colHeader}>
                  🇬🇧 English
                  <span style={s.count}>{textToList(enText).length} words</span>
                </div>
                <div style={s.searchRow}>
                  <span style={s.searchIcon}>🔍</span>
                  <input
                    type="text"
                    value={enSearch}
                    onChange={e => upperInputChange(e, setEnSearch)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && enSearch.length > 0 && enMatches.length === 0) {
                        setEnText(t => t ? t + '\n' + enSearch : enSearch)
                        setEnSearch('')
                      }
                    }}
                    placeholder="Search existing word…"
                    style={s.searchInput}
                    spellCheck={false}
                  />
                  {enSearch.length > 0 && (
                    <button onClick={() => setEnSearch('')} style={s.clearBtn}>✕</button>
                  )}
                </div>
                {enSearch.length > 0 && (
                  <div style={s.searchResults}>
                    {enMatches.length === 0
                      ? <span style={s.noMatch}>No matches — press Enter to add</span>
                      : enMatches.map(w => <span key={w} style={s.matchChip}>{w}</span>)
                    }
                  </div>
                )}
                <textarea
                  style={s.textarea}
                  value={enText}
                  onChange={(e) => upperChange(e, setEnText)}
                  placeholder={'SUN\nSEA\nHOME\n…'}
                  spellCheck={false}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  savedBadge: {
    fontSize:   '0.88rem',
    fontWeight: 700,
    color:      '#52B788',
  },
  errorBanner: {
    backgroundColor: '#FEE2E2',
    color:           '#991B1B',
    border:          '1px solid #FCA5A5',
    borderRadius:    10,
    padding:         '12px 16px',
    marginBottom:    20,
    fontSize:        '0.9rem',
  },
  hint: {
    fontSize: '0.875rem',
    color:        '#9A8070',
    fontStyle:    'italic',
    lineHeight:   1.5,
    marginBottom: 20,
  },
  grid: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gap:                 20,
  },
  col: {
    display:       'flex',
    flexDirection: 'column',
    gap:           8,
  },
  colHeader: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    fontSize:       '0.9rem',
    fontWeight:     700,
    color:          '#1B4332',
  },
  count: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color:      '#9A8070',
  },
  searchRow: {
    display:      'flex',
    alignItems:   'center',
    gap:          6,
    background:   '#F5F0EA',
    border:       '1.5px solid #D8C9B8',
    borderRadius: 8,
    padding:      '0 10px',
  },
  searchIcon: {
    fontSize:   '0.9rem',
    flexShrink: 0,
  },
  searchInput: {
    flex:       1,
    border:     'none',
    background: 'transparent',
    outline:    'none',
    fontSize:   '0.9rem',
    fontFamily: 'monospace',
    color:      '#2E1F14',
    padding:    '9px 0',
    minHeight:  40,
  },
  clearBtn: {
    background: 'transparent',
    border:     'none',
    cursor:     'pointer',
    color:      '#9A8070',
    fontSize: '0.875rem',
    padding:    '2px 4px',
    flexShrink: 0,
  },
  searchResults: {
    display:      'flex',
    flexWrap:     'wrap',
    gap:          4,
    padding:      '6px 8px',
    background:   '#FFFBEB',
    border:       '1.5px solid #F59E0B',
    borderRadius: 8,
    minHeight:    36,
    alignItems:   'center',
  },
  noMatch: {
    fontSize: '0.875rem',
    color:     '#52B788',
    fontStyle: 'italic',
  },
  matchChip: {
    backgroundColor: '#FDE68A',
    color:           '#78350F',
    borderRadius:    20,
    padding:         '2px 10px',
    fontSize: '0.875rem',
    fontWeight:      700,
    border:          '1px solid #F59E0B',
  },
  textarea: {
    width:        '100%',
    minHeight:    520,
    fontSize:     '0.88rem',
    fontFamily:   'monospace',
    padding:      '12px',
    border:       '1.5px solid #D8C9B8',
    borderRadius: 10,
    background:   '#FAF6EF',
    color:        '#2E1F14',
    resize:       'vertical',
    lineHeight:   1.6,
    boxSizing:    'border-box',
  },
}
